/**
 * CaseTime Wishlist
 * localStorage-backed wishlist with custom element, aria-pressed hearts,
 * header count badge, and a drawer that fetches live product data.
 *
 * Storage key : casetime:wishlist:v1
 * Event       : casetime:wishlist:change (detail: { ids: string[] })
 * Max items   : 24
 */

const STORAGE_KEY = 'casetime:wishlist:v1';
const MAX_ITEMS = 24;
const CHANGE_EVENT = 'casetime:wishlist:change';
const labels = document.querySelector('casetime-wishlist-drawer')?.dataset ?? {};

/* ─── Storage helpers ─── */
function readIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeIds(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* storage full — ignore */
  }
}

function dispatchChange(ids) {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { ids } }));
}

function toggleId(id) {
  const ids = readIds();
  const idx = ids.indexOf(id);
  let next;
  if (idx === -1) {
    next = [id, ...ids].slice(0, MAX_ITEMS);
  } else {
    next = ids.filter((x) => x !== id);
  }
  writeIds(next);
  dispatchChange(next);
  return next;
}

function isInWishlist(id) {
  return readIds().includes(id);
}

/* ─── Fetch live product data for a list of IDs ─── */
async function fetchProducts(ids) {
  if (!ids.length) return [];
  const query = ids.map((id) => `id:${id}`).join(' OR ');
  try {
    const resp = await fetch(
      `${window.Shopify?.routes?.root ?? '/'}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${ids.length}`,
    );
    if (!resp.ok) return [];
    const json = await resp.json();
    return json.resources?.results?.products ?? [];
  } catch {
    return [];
  }
}

/* ─── <casetime-wishlist-btn> ─── */
class CasetimeWishlistBtn extends HTMLElement {
  connectedCallback() {
    this._id = this.dataset.productId;
    this._update();
    document.addEventListener(CHANGE_EVENT, () => this._update());
    this.addEventListener('click', this._toggle.bind(this));
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._toggle();
      }
    });
  }

  _update() {
    const saved = isInWishlist(this._id);
    this.setAttribute('aria-pressed', String(saved));
    this.setAttribute(
      'aria-label',
      saved
        ? labels.removeLabel
        : labels.addLabel,
    );
    const icon = this.querySelector('.ct-wishlist-icon');
    if (icon) {
      icon.innerHTML = saved ? HEART_FILLED : HEART_EMPTY;
    }
  }

  _toggle(event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this._id) return;

    toggleId(this._id);
  }
}

/* ─── <casetime-wishlist-count> — header badge ─── */
class CasetimeWishlistCount extends HTMLElement {
  connectedCallback() {
    this._update();
    document.addEventListener(CHANGE_EVENT, () => this._update());
  }

  _update() {
    const count = readIds().length;
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
    this.setAttribute('aria-label', (labels.countLabel ?? '').replace('[count]', String(count)));
  }
}

/* ─── <casetime-wishlist-drawer> ─── */
class CasetimeWishlistDrawer extends HTMLElement {
  connectedCallback() {
    this._list = this.querySelector('[data-wishlist-list]');
    this._empty = this.querySelector('[data-wishlist-empty]');
    this._closeBtn = this.querySelector('[data-wishlist-close]');
    this._closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener(CHANGE_EVENT, () => {
      if (!this.hidden) this._render();
    });
    // Close on backdrop click
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });
    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    });
  }

  open() {
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this._closeBtn?.focus();
    this._render();
  }

  close() {
    this.hidden = true;
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // Return focus to the trigger
    document.querySelector('[data-wishlist-trigger]')?.focus();
  }

  async _render() {
    const ids = readIds();
    if (!this._list) return;
    if (ids.length === 0) {
      this._list.innerHTML = '';
      if (this._empty) this._empty.hidden = false;
      return;
    }
    if (this._empty) this._empty.hidden = true;
    this._list.innerHTML = `<li class="ct-wishlist-loading">${escapeHtml(labels.loadingLabel ?? '')}</li>`;
    const products = await fetchProducts(ids);
    // Filter out unavailable (handle stale IDs gracefully)
    const validIds = products.map((p) => String(p.id));
    const staleIds = ids.filter((id) => !validIds.includes(String(id)));
    if (staleIds.length) {
      const cleaned = ids.filter((id) => !staleIds.includes(id));
      writeIds(cleaned);
    }
    if (products.length === 0) {
      this._list.innerHTML = '';
      if (this._empty) this._empty.hidden = false;
      return;
    }
    this._list.innerHTML = products
      .map(
        (p) => `
      <li class="ct-wishlist-item">
        <a href="${p.url}" class="ct-wishlist-item-img-link" tabindex="-1" aria-hidden="true">
          <img src="${p.featured_image?.url ?? ''}" alt="${escapeHtml(p.title)}" width="80" height="80" loading="lazy" class="ct-wishlist-item-img">
        </a>
        <div class="ct-wishlist-item-info">
          <a href="${p.url}" class="ct-wishlist-item-title">${escapeHtml(p.title)}</a>
          <p class="ct-wishlist-item-price">${p.price_min ? formatMoney(p.price_min) : ''}</p>
          ${p.available === false ? `<p class="ct-wishlist-item-unavailable">${escapeHtml(labels.unavailableLabel ?? '')}</p>` : ''}
        </div>
        <casetime-wishlist-btn data-product-id="${p.id}" class="ct-wishlist-item-remove" aria-label="${escapeHtml(labels.removeLabel ?? '')}">
          <span class="ct-wishlist-icon">${HEART_FILLED}</span>
        </casetime-wishlist-btn>
      </li>`,
      )
      .join('');
  }
}

/* ─── Drawer trigger ─── */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-wishlist-trigger]');
  if (!trigger) return;
  e.preventDefault();
  const drawer = document.querySelector('casetime-wishlist-drawer');
  drawer?.open();
});

/* ─── Sync hearts on page load ─── */
document.addEventListener('DOMContentLoaded', () => {
  dispatchChange(readIds());
});

/* ─── SVG icons ─── */
const HEART_EMPTY = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>`;
const HEART_FILLED = `<svg width="20" height="20" viewBox="0 0 24 24" fill="#250256" stroke="#250256" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>`;

/* ─── Utilities ─── */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  return `${window.Shopify?.currency?.active ?? ''} ${amount}`.trim();
}

/* ─── Register custom elements ─── */
customElements.define('casetime-wishlist-btn', CasetimeWishlistBtn);
customElements.define('casetime-wishlist-count', CasetimeWishlistCount);
customElements.define('casetime-wishlist-drawer', CasetimeWishlistDrawer);
